/* Default bundles and custom accessories, with exact-variant bundle quotes. */
(() => {
  'use strict';
  const start = () => {
    const root = document.querySelector('[data-bundle-accessories]');
    if (!root || root.dataset.ready) return;
    root.dataset.ready = 'true';
    const page = root.closest('[data-elite-bundles]');
    const listing = page.querySelector('[data-elite-collection-grid]');
    const dialog = root.querySelector('dialog');
    const results = root.querySelector('[data-bundle-picker-products]');
    const status = root.querySelector('[data-bundle-picker-status]');
    const search = root.querySelector('[data-bundle-picker-search]');
    const categorySelect = root.querySelector('[data-bundle-picker-category]');
    const pickerBody = root.querySelector('[data-bundle-picker-body]');
    const selectionRow = root.querySelector('[data-bundle-accessory-selection]');
    const clear = root.querySelector('[data-bundle-accessory-clear]');
    const categories = [...root.querySelectorAll('[data-bundle-accessory-category]')];
    const names = new Map(categories.map(button => [button.dataset.bundleAccessoryCategory, button.querySelector('span').textContent]));
    const patterns = {
      cables:/^cables?$/i, desk_pad:/^desk pads?$/i, earphones:/^earphones?$/i,
      gaming_controller:/^gaming controllers?$/i, headphones:/^headphones?$/i, headset:/^headsets?$/i,
      keyboard_mouse:/^keyboards?\s*(?:&|and)\s*(?:mouse|mice)(?:\s+sets?)?$/i, keyboard:/^keyboards?$/i,
      loudspeaker:/^(?:loud)?speakers?$/i, mouse:/^(?:mice|mouse)$/i, microphone:/^microphones?$/i,
      monitors:/^monitors?$/i, mouse_pad:/^mouse pads?$/i, webcam:/^webcams?$/i, wrist_rest:/^wrist rests?$/i
    };
    const hardware = new Set(['case','mobo','cpu','ram','gpu','psu','cooler','wifi','sata_ssd','sata_hdd','nvme_ssd','windows_key','kettle_lead']);
    const money = pennies => new Intl.NumberFormat('en-GB',{style:'currency',currency:'GBP'}).format(pennies / 100);
    const selected = new Map();
    const cardStates = new WeakMap();
    const near = new WeakSet();
    const watched = new WeakSet();
    let options = [], catalogRequest, activeCategory = '', opener, previousOverflow = '', timer, revision = 0;
    let quoteTimer, quoteRunning = false, quoteAgain = false;
    const text = (node, value) => { if (node && node.textContent !== value) node.textContent = value; };
    const button = (label, action) => { const node = document.createElement('button'); node.type = 'button'; node.textContent = label; node.addEventListener('click',action); return node; };
    const picture = (src, alt = '') => { const node = document.createElement('img'); node.src = src; node.alt = alt; node.loading = 'lazy'; node.decoding = 'async'; return node; };
    const publicImage = source => {
      try { const url = new URL(source,location.origin); return ['https:','http:'].includes(url.protocol) ? url.href : ''; } catch { return ''; }
    };
    const categoryFor = value => {
      let labels;
      try { labels = JSON.parse(value); } catch { labels = String(value || '').split('|'); }
      return Object.keys(patterns).find(key => (Array.isArray(labels) ? labels : [labels]).some(label => patterns[key].test(String(label).trim().replace(/[-_]+/g,' '))));
    };
    const priceFor = option => Math.round((window.ElitePCPriceAddition?.accessoryPrice(option.component,{amount:option.price / 100},option.sku)?.amount ?? option.price / 100) * 100);
    const selectedSignature = () => JSON.stringify([...selected.keys()].sort());
    const lines = () => [...selected.values()].map(({component,sku,variantId}) => ({component,sku,variantId}));

    async function loadCatalog() {
      if (catalogRequest) return catalogRequest;
      catalogRequest = (async () => {
        const load = async number => {
          const url = new URL('/collections/accessories',location.origin);
          url.searchParams.set('section_id','elite-bundle-accessory-catalog');
          url.searchParams.set('filter.v.availability','1');
          url.searchParams.set('page',String(number));
          const response = await fetch(url,{signal:AbortSignal.timeout(20000),credentials:'same-origin'});
          if (!response.ok) throw new Error('Accessories could not be loaded.');
          const doc = new DOMParser().parseFromString(await response.text(),'text/html');
          const data = JSON.parse(doc.querySelector('[data-bundle-accessory-catalog]')?.textContent || 'null');
          if (!Array.isArray(data?.products) || !Number.isInteger(data.pages) || data.pages < 0 || data.pages > 50) throw new Error('Accessories could not be loaded.');
          return data;
        };
        const first = await load(1), products = new Map(first.products.map(product => [product.id,product]));
        for (let next = 2; next <= first.pages; next += 2) {
          const pages = await Promise.all([load(next),...(next < first.pages ? [load(next + 1)] : [])]);
          for (const data of pages) for (const product of data.products) products.set(product.id,product);
        }
        if (Number.isInteger(first.total) && first.total !== products.size) throw new Error('The accessories list is incomplete. Please try again.');
        const variants = new Map();
        for (const product of products.values()) {
          const component = categoryFor(product.category);
          if (!component) continue;
          for (const variant of product.variants || []) {
            if (variant.available !== true || !variant.sku || !/^\d+$/.test(variant.id) || !Number.isSafeInteger(variant.price) || variant.price < 0) continue;
            const title = product.title + (variant.title && variant.title !== 'Default Title' ? ` — ${variant.title}` : '');
            variants.set(variant.id,{component,sku:variant.sku,variantId:variant.id,title,price:variant.price,image:publicImage(variant.image),url:product.url});
          }
        }
        options = [...variants.values()];
        return options;
      })().catch(error => { catalogRequest = null; throw error; });
      return catalogRequest;
    }

    function syncLocation() {
      const url = new URL(location.href);
      for (const key of [...url.searchParams.keys()]) if (key.startsWith('bundle_') && patterns[key.slice(7)]) url.searchParams.delete(key);
      url.searchParams.delete('bundle_accessories');
      if (!selected.size) url.searchParams.set('bundle_accessories','none');
      for (const option of selected.values()) url.searchParams.append('bundle_' + option.component,option.sku);
      if (url.href !== location.href) history.replaceState(history.state,'',url.pathname + url.search + url.hash);
    }
    function updateSelection(writeLocation = true) {
      revision++;
      for (const tile of categories) {
        const count = [...selected.values()].filter(option => option.component === tile.dataset.bundleAccessoryCategory).length;
        tile.setAttribute('aria-pressed',String(count > 0));
        const badge = tile.querySelector('[data-bundle-category-count]');
        badge.hidden = !count; text(badge,String(count));
      }
      selectionRow.replaceChildren();
      for (const option of selected.values()) {
        const chip = document.createElement('div'); chip.className = 'epc-bundle-accessories__chip';
        if (option.image) chip.append(picture(option.image));
        const name = document.createElement('span'); name.textContent = option.title;
        const remove = button('×',() => { selected.delete(option.variantId); updateSelection(); });
        remove.setAttribute('aria-label','Remove ' + option.title); chip.append(name,remove); selectionRow.append(chip);
      }
      selectionRow.hidden = !selected.size; clear.hidden = !selected.size;
      const count = selected.size;
      const description = count ? `${count} accessor${count === 1 ? 'y' : 'ies'} selected · added to every PC below. Bundle prices include your selected gear.` : 'Select an accessory type to add products. Your choices apply to every PC below.';
      text(root.querySelector('[data-bundle-accessory-status]'),description);
      text(root.querySelector('[data-bundle-picker-total]'),count ? `${count} selected · ${money([...selected.values()].reduce((sum,option) => sum + priceFor(option),0))} of accessories` : 'No accessories selected');
      if (writeLocation) syncLocation();
      if (dialog.open) renderOptions();
      syncCards();
    }
    function toggle(option) {
      if (selected.has(option.variantId)) selected.delete(option.variantId);
      else {
        if (selected.size >= 15) { text(status,'You can add up to 15 accessories. Remove one to add another.'); return; }
        selected.set(option.variantId,option);
      }
      updateSelection();
    }
    function renderOptions() {
      const terms = search.value.toLowerCase().trim().split(/\s+/).filter(Boolean);
      const matching = options.filter(option => option.component === activeCategory && terms.every(term => option.title.toLowerCase().includes(term)));
      text(status,activeCategory === 'cables' ? 'Cables are sold separately. Choose a cable to view its product page.' : matching.length ? `${matching.length} in-stock ${names.get(activeCategory)?.toLowerCase()}. Select products to add them to your bundle.` : 'No matching in-stock accessories. Try a different search.');
      const fragment = document.createDocumentFragment();
      for (const option of matching) {
        const card = document.createElement('article'); card.className = 'epc-bundle-picker__product'; card.dataset.selected = String(selected.has(option.variantId));
        if (option.image) card.append(picture(option.image));
        const title = document.createElement('h3'); title.textContent = option.title;
        const price = document.createElement('strong'); price.textContent = money(priceFor(option));
        card.append(title,price);
        if (activeCategory === 'cables') {
          const link = document.createElement('a'); link.textContent = 'View cable'; link.href = option.url; link.target = '_blank'; link.rel = 'noopener'; card.append(link);
        } else {
          const added = selected.has(option.variantId);
          const add = button(added ? '✓ Added — remove' : 'Add to bundle',() => toggle(option));
          add.setAttribute('aria-label',(added ? 'Remove ' : 'Add ') + option.title + (added ? ' from bundle' : ' to bundle'));
          add.setAttribute('aria-pressed',String(added)); card.append(add);
        }
        fragment.append(card);
      }
      results.replaceChildren(fragment);
    }
    async function openPicker(category, trigger, focusSearch = true) {
      if (!names.has(category)) return;
      activeCategory = category; opener = trigger; search.value = ''; categorySelect.value = category;
      pickerBody.scrollTop = 0;
      text(root.querySelector('[data-bundle-picker-title]'),'Choose ' + names.get(category).toLowerCase());
      text(status,'Loading in-stock accessories…'); results.replaceChildren();
      if (!dialog.open) { previousOverflow = document.documentElement.style.overflow; dialog.showModal(); document.documentElement.style.overflow = 'hidden'; }
      if (focusSearch) search.focus({preventScroll:true});
      try { await loadCatalog(); if (dialog.open) renderOptions(); }
      catch { text(status,'Accessories could not be loaded.'); status.append(button('Try again',() => openPicker(category,trigger))); }
    }
    const closePicker = () => dialog.close();
    root.querySelector('[data-bundle-picker-close]').addEventListener('click',closePicker);
    root.querySelector('[data-bundle-picker-done]').addEventListener('click',closePicker);
    dialog.addEventListener('close',() => { document.documentElement.style.overflow = previousOverflow; opener?.focus({preventScroll:true}); });
    dialog.addEventListener('click',event => { if (event.target === dialog) { const box = dialog.getBoundingClientRect(); if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) closePicker(); } });
    for (const tile of categories) tile.addEventListener('click',() => openPicker(tile.dataset.bundleAccessoryCategory,tile));
    search.addEventListener('input',renderOptions);
    categorySelect.addEventListener('change',() => openPicker(categorySelect.value,opener,false));
    clear.addEventListener('click',() => { selected.clear(); updateSelection(); });

    function recipe(card) {
      const anchor = card.querySelector('.epc-spotlight__button--configure');
      if (!anchor) return null;
      const url = new URL(anchor.href,location.origin), selections = {};
      for (const key of hardware) { const sku = url.searchParams.get('elite_' + key); if (sku && !sku.startsWith('__no_')) selections[key] = sku; }
      return selections.case && ['mobo','cpu','ram','gpu','psu'].every(key => selections[key]) ? selections : null;
    }
    function decorate(card, state) {
      const extras = [...selected.values()];
      card.dataset.bundleHasExtras = String(extras.length > 0);
      for (const anchor of card.querySelectorAll('a[href*="/products/"]')) {
        const url = new URL(anchor.href,location.origin);
        for (const key of Object.keys(patterns)) url.searchParams.delete('elite_' + key);
        for (const option of extras) url.searchParams.append('elite_' + option.component,option.sku);
        const href = url.pathname + url.search + url.hash;
        if (anchor.getAttribute('href') !== href) anchor.setAttribute('href',href);
      }
      const signature = selectedSignature();
      if (state.decorated === signature) return;
      state.decorated = signature;
      card.querySelectorAll('.epc-bundle-card__gear,.epc-bundle-card__summary,.epc-bundle-card__pricing').forEach(node => node.remove());
      if (!extras.length) return;
      const gear = document.createElement('div'); gear.className = 'epc-bundle-card__gear'; gear.dataset.many = String(extras.length > 3);
      gear.setAttribute('aria-label','Included accessories');
      for (const option of extras) if (option.image) { const img = picture(option.image,option.title); img.title = option.title; gear.append(img); }
      card.querySelector('[data-spotlight-gallery]')?.append(gear);
      const summary = document.createElement('p'); summary.className = 'epc-bundle-card__summary';
      summary.textContent = 'Includes: ' + extras.map(option => option.title).join(' · ');
      const pricing = document.createElement('div'); pricing.className = 'epc-bundle-card__pricing'; pricing.dataset.state = 'pending';
      const label = document.createElement('small'); label.textContent = 'Full bundle price';
      const value = document.createElement('strong');
      value.style.minHeight = '1.2em'; value.style.minWidth = '6.5ch'; value.style.display = 'inline-block';
      pricing.setAttribute('aria-busy','true'); pricing.append(label,value);
      card.querySelector('[data-elite-pc-card-pricing]')?.after(summary,pricing);
    }
    const observe = typeof window.IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
      for (const entry of entries) if (entry.isIntersecting) { near.add(entry.target); } else near.delete(entry.target);
      scheduleQuotes();
    },{rootMargin:'200px'}) : null;
    function syncCards() {
      if (!listing) return;
      for (const card of listing.querySelectorAll('epc-featured-spotlight')) {
        let state = cardStates.get(card);
        if (!state) { state = {}; cardStates.set(card,state); }
        if (!watched.has(card)) { watched.add(card); if (observe) observe.observe(card); else near.add(card); }
        decorate(card,state);
        const parts = recipe(card);
        const signature = JSON.stringify([parts,selectedSignature()]);
        if (state.signature !== signature) {
          state.signature = signature; state.selections = parts; state.quote = null; state.failed = false;
          const pricing = card.querySelector('.epc-bundle-card__pricing');
          if (pricing) { pricing.dataset.state = 'pending'; pricing.setAttribute('aria-busy','true'); }
        }
      }
      if (selected.size) syncLocation();
      scheduleQuotes();
    }
    function scheduleQuotes() { clearTimeout(quoteTimer); quoteTimer = setTimeout(runQuotes,140); }
    async function runQuotes() {
      if (quoteRunning) { quoteAgain = true; return; }
      if (!selected.size || !window.ElitePCPrivate?.fit) return;
      const groups = new Map(), extras = lines(), runRevision = revision;
      for (const card of listing.querySelectorAll('epc-featured-spotlight')) {
        const state = cardStates.get(card);
        if (!near.has(card) || card.closest('[hidden]') || !state?.selections || state.quote || state.failed) continue;
        const key = JSON.stringify(Object.entries(state.selections).filter(([key]) => key !== 'case').sort(([a],[b]) => a.localeCompare(b)));
        if (!groups.has(key)) groups.set(key,[]);
        groups.get(key).push({card,state,signature:state.signature});
      }
      quoteRunning = true;
      try {
        for (const group of groups.values()) for (let offset = 0; offset < group.length; offset += 12) {
          if (revision !== runRevision) return;
          const batch = group.slice(offset,offset + 12);
          try {
            const answer = await (window.ElitePCPrivate.prices || window.ElitePCPrivate.fit)({selections:batch[0].state.selections,component:'case',skus:batch.map(item => item.state.selections.case),accessories:extras});
            for (const {card,state,signature} of batch) {
              if (revision !== runRevision || state.signature !== signature || !card.isConnected) continue;
              const option = answer.options?.find(option => option.sku === state.selections.case);
              const price = option?.price;
              const pricing = card.querySelector('.epc-bundle-card__pricing');
              const valid = price?.currency === 'GBP' && Number.isFinite(price.amount) && price.amount > 0;
              state.quote = valid ? price : null; state.failed = !valid;
              if (pricing) { pricing.dataset.state = valid ? 'ready' : 'error'; pricing.setAttribute('aria-busy','false'); text(pricing.querySelector('strong'),valid ? money(Math.round(price.amount * 100)) : 'Check bundle in configurator'); }
            }
          } catch {
            for (const {card,state,signature} of batch) if (revision === runRevision && state.signature === signature) {
              state.failed = true; const pricing = card.querySelector('.epc-bundle-card__pricing');
              if (pricing) { pricing.dataset.state = 'error'; pricing.setAttribute('aria-busy','false'); text(pricing.querySelector('strong'),'Check bundle in configurator'); }
            }
          }
        }
      } finally { quoteRunning = false; if (quoteAgain || runRevision !== revision) { quoteAgain = false; scheduleQuotes(); } }
    }
    if (listing) {
      new MutationObserver(() => { clearTimeout(timer); timer = setTimeout(syncCards,80); }).observe(listing,{subtree:true,childList:true,attributes:true,attributeFilter:['href','hidden']});
      // The configurator shows the complete bundle, including accessory choices.
      listing.addEventListener('click',event => { if (selected.size && event.target.closest('[data-spotlight-details]')) event.stopPropagation(); },true);
    }
    async function restore() {
      const params = new URLSearchParams(location.search);
      const requested = Object.keys(patterns).filter(key => key !== 'cables').flatMap(component => params.getAll('bundle_' + component).map(sku => ({component,sku})));
      if (!requested.length) {
        selected.clear();
        if (params.get('bundle_accessories') === 'none') { updateSelection(false); return; }
        try {
          const defaults = JSON.parse(root.querySelector('[data-bundle-accessory-defaults]')?.textContent || '[]');
          for (const option of defaults) if (['mouse','headset','keyboard'].includes(option.component) && option.sku && /^\d+$/.test(option.variantId) && Number.isSafeInteger(option.price)) selected.set(option.variantId,{...option,image:publicImage(option.image)});
          // If a configured default sells out, use an available item of that type.
          if (['mouse','headset','keyboard'].some(component => ![...selected.values()].some(option => option.component === component))) {
            await loadCatalog();
            for (const component of ['mouse','headset','keyboard']) if (![...selected.values()].some(option => option.component === component)) {
              const option = options.find(option => option.component === component);
              if (option) selected.set(option.variantId,option);
            }
          }
          updateSelection();
        } catch { updateSelection(false); text(root.querySelector('[data-bundle-accessory-status]'),'Default bundle accessories could not be loaded. Choose accessories above to build your bundle.'); }
        return;
      }
      try {
        await loadCatalog(); selected.clear();
        for (const request of requested.slice(0,15)) { const option = options.find(option => option.component === request.component && option.sku === request.sku); if (option) selected.set(option.variantId,option); }
        updateSelection(false);
      } catch { text(root.querySelector('[data-bundle-accessory-status]'),'Your saved accessories could not be loaded. Choose an accessory type to try again.'); }
    }
    window.addEventListener('popstate',restore);
    void restore();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',start,{once:true}); else start();
})();
